import {
  API_ROUTES,
  addChatDocument,
  getChatDocument,
  postEncryptedJson,
} from "../utils/apiClient";
import AppConstants from "../utils/appConstants";

function pickSenderRoot(m) {
  if (!m || typeof m !== "object") return null;
  return (
    m.sender ??
    m.SENDER ??
    m.sender_device ??
    m.senderDevice ??
    m.SENDER_DEVICE ??
    (m.user_login || m.USER || m.user || m.userLogin ? m : null)
  );
}

function pickFirstDefined(sources, keys) {
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    for (const key of keys) {
      const val = src[key];
      if (val != null && val !== "") return val;
    }
  }
  return null;
}

/**
 * Same idea as Flutter ChatModel: sender.user_login.ID
 */
function extractSenderUserIdFromRaw(m) {
  if (!m || typeof m !== "object") return null;

  const topLevel =
    m.SENDER_USER_ID ??
    m.SENDER_ID ??
    m.sender_id ??
    m.SENDER_LOGIN_ID ??
    m.LOGIN_ID ??
    null;
  if (topLevel != null && topLevel !== "") return String(topLevel);

  const s = pickSenderRoot(m);
  if (!s || typeof s !== "object") return null;
  const ul = s.user_login ?? s.USER ?? s.user ?? s.userLogin;
  if (ul && typeof ul === "object") {
    const id = ul.ID ?? ul.id ?? ul.USER_ID ?? ul.user_id;
    if (id != null && id !== "") return String(id);
  }
  const directId = s.ID ?? s.id ?? s.USER_ID ?? s.user_id;
  if (directId != null && directId !== "") return String(directId);
  return null;
}

function extractSenderNickFromRaw(m) {
  const s = pickSenderRoot(m);
  if (!s || typeof s !== "object") return "";
  const ul = s.user_login ?? s.USER ?? s.user ?? s.userLogin;
  if (ul && typeof ul === "object") {
    return String(
      ul.NICK_NAME ??
        ul.nick_name ??
        ul.USERNAME ??
        ul.username ??
        ul.EMAIL ??
        ul.email ??
        ""
    ).trim();
  }
  return String(s.NICK_NAME ?? s.nick_name ?? s.USERNAME ?? s.username ?? "").trim();
}

export function mapRawChatRowToMessage(m) {
  const nested =
    m.chat_message || m.chatMessage || m.CHAT_MESSAGE || m.message || null;
  const sources = [m, nested].filter((src) => src && typeof src === "object");

  const text =
    m.MESSAGE ??
    (typeof m.message === "string" ? m.message : null) ??
    (nested && typeof nested.MESSAGE === "string" ? nested.MESSAGE : null) ??
    (nested && typeof nested.message === "string" ? nested.message : null) ??
    "";

  const isFromUser = pickFirstDefined(sources, [
    "IS_FROM_USER",
    "is_from_user",
    "IsFromUser",
  ]);
  const fromUser = pickFirstDefined(sources, ["FROM_USER", "from_user", "FromUser"]);
  const isMeSender = pickFirstDefined(sources, [
    "IS_ME_SENDER",
    "is_me_sender",
    "IsMeSender",
  ]);
  const isSupportMessage = pickFirstDefined(sources, [
    "IS_SUPPORT",
    "is_support",
    "IS_SUPPORT_MESSAGE",
    "is_support_message",
    "FROM_SUPPORT",
    "from_support",
    "SENT_BY_SUPPORT",
    "sent_by_support",
  ]);

  const senderType =
    pickFirstDefined(sources, [
      "SENDER_TYPE",
      "sender_type",
      "MESSAGE_FROM",
      "message_from",
    ]) ?? null;

  const messageType = pickFirstDefined(sources, [
    "MESSAGE_TYPE",
    "message_type",
  ]);

  let userId = pickFirstDefined(sources, ["USER_ID", "user_id"]);
  if (userId == null) {
    for (const src of sources) {
      const u = src.USER ?? src.user;
      if (u && typeof u === "object") {
        const id = u.ID ?? u.id;
        if (id != null && id !== "") {
          userId = id;
          break;
        }
      }
    }
  }

  const createdAt =
    pickFirstDefined(sources, [
      "CREATED_AT",
      "CREATED_AT_DATE",
      "created_at",
      "DATE",
      "date",
    ]) ?? null;

  let senderUserId = null;
  let senderNickName = "";
  for (const src of sources) {
    const sid = extractSenderUserIdFromRaw(src);
    if (sid) {
      senderUserId = sid;
      senderNickName = extractSenderNickFromRaw(src);
      break;
    }
  }

  return {
    id: m.ID ?? m.id ?? m.MESSAGE_ID ?? m.messageId ?? m.CHAT_ID ?? m.chatID ?? null,
    text,
    fromUser,
    isFromUser,
    isMeSender,
    isSupportMessage,
    senderType,
    messageType,
    userId,
    senderUserId,
    senderNickName,
    createdAt,
    raw: m,
  };
}

/**
 * Chat Controller
 * Encapsulates support chat APIs (get messages, send message).
 * Uses encrypted transport + Bearer token via postEncryptedJson (matches Flutter behavior).
 */
class ChatController {
  /**
   * Fetch chat messages.
   *
   * @param {Object} params
   * @param {number} [params.page=1] - 1-based page index (sent as ?page=).
   * @param {number} [params.perPage=20] - Messages per page.
   * @param {Object} [options] - Optional postEncryptedJson options (e.g., baseURL override).
   *
   * @returns {Promise<{
   *   messages: Array,
   *   pagination: { page: number, perPage: number, total?: number, lastPage?: number },
   *   raw: any
   * }>}
   */
  static async getMessages({ page = 1, perPage = 20 } = {}, options = {}) {
    const endpoint = `${API_ROUTES.GET_CHAT_MESSAGE}?page=${page}`;

    const body = {
      PER_PAGE: perPage,
    };

    const res = await postEncryptedJson(endpoint, body, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const dataBlock = d.DATA || {};
    let list = [];

    if (Array.isArray(dataBlock.data)) {
      list = dataBlock.data;
    } else if (Array.isArray(d.DATA)) {
      list = d.DATA;
    } else if (Array.isArray(dataBlock.items)) {
      list = dataBlock.items;
    } else if (Array.isArray(dataBlock.messages)) {
      list = dataBlock.messages;
    }

    const messages = list.map((m) => mapRawChatRowToMessage(m));

    const currentPage = Number(dataBlock.current_page ?? page ?? 1);
    const lastPage = Number(dataBlock.last_page ?? currentPage);
    const perPageVal = Number(dataBlock.per_page ?? perPage) || perPage;
    const totalVal = Number(dataBlock.total ?? dataBlock.TOTAL ?? messages.length);

    const pagination = {
      page: currentPage,
      perPage: perPageVal,
      total: totalVal,
      lastPage,
    };

    return { messages, pagination, raw: d };
  }

  /**
   * Single message by CHAT_ID — same as Flutter APIURI.GET_CHAT_MESSAGE (get-a-message).
   */
  static async getMessageByChatId(chatId, options = {}) {
    const id = chatId != null ? String(chatId).trim() : "";
    if (!id) {
      throw new Error("CHAT_ID is required.");
    }

    const res = await postEncryptedJson(API_ROUTES.GET_A_CHAT_MESSAGE, { CHAT_ID: id }, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const row = d.DATA;
    if (!row || typeof row !== "object") {
      throw new Error("Invalid message payload.");
    }

    return { message: mapRawChatRowToMessage(row), raw: d };
  }

  /**
   * Send a new chat message.
   *
   * @param {Object} params
   * @param {string} params.message - Message text.
   * @param {number|string|null} [params.userId=null] - Optional USER_ID to send with the message.
   * @param {Object} [options] - Optional postEncryptedJson options.
   *
   * @returns {Promise<{ raw: any }>}
   */
  static async sendMessage({ message, userId = null }, options = {}) {
    if (!message || typeof message !== "string" || !message.trim()) {
      throw new Error("Message text is required.");
    }

    const body = {
      MESSAGE: message.trim(),
      USER_ID:
        userId != null
          ? (typeof userId === "string" ? userId : String(userId))
          : null,
    };

    const res = await postEncryptedJson(API_ROUTES.ADD_CHAT_MESSAGE, body, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    return { raw: d };
  }

  /**
   * Upload a chat document.
   * @param {Object} params
   * @param {File|Blob} params.file
   * @param {number} [params.chatType=0]
   * @param {number} [params.messageType=1]
   */
  static async uploadChatDocument(
    { file, chatType = 0, messageType = 3 } = {},
    options = {}
  ) {
    const response = await addChatDocument(file, { chatType, messageType }, options);
    const d = response?.data || {};
    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }
    return { raw: d };
  }

  /**
   * Download a chat document blob and provide an object URL for preview/download.
   */
  static async getChatDocumentUrl(documentName, options = {}) {
    const response = await getChatDocument(documentName, options);
    const blob = response?.data;
    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error("Invalid document payload.");
    }
    return {
      blob,
      url: URL.createObjectURL(blob),
      contentType: blob.type || "application/octet-stream",
    };
  }
}

export default ChatController;
